/* import __COLOCATED_TEMPLATE__ from './priority-selector.hbs'; */
/* RESPONSIBLE TEAM: team-inbox-experience */

import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import type Session from 'embercom/services/session';
import { type BooleanComparison } from 'embercom/objects/inbox/search/predicate-group';
import {
  PRIORITY_LEVEL_PICKER_ORDER,
  priorityLevelDisplay,
  type PriorityLevelName,
} from 'embercom/lib/inbox/priority-levels';
import { type PriorityComparison } from 'embercom/components/inbox2/search/priority-level-filter';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';

// Selection sentinel for "no filter applied". Used both for the popover-open
// state and as the cleared value. Kept separate from the literal level/binary
// values so it survives the discriminated union in `search2.ts#selectPriority`.
const NONE_SENTINEL = 'none';

// The selector emits either a legacy BooleanComparison (binary saved views) or
// a numeric level (multi-level mode). Consumers decide which predicate shape
// to add — see `search2.ts#selectPriority`.
export type PrioritySelectorValue = BooleanComparison | number;

interface Args {
  selected?: PrioritySelectorValue;
  selectedComparison?: PriorityComparison;
  onSelect: (priority?: PrioritySelectorValue, comparison?: PriorityComparison) => void;
}

interface Signature {
  Args: Args;
  Element: HTMLElement;
}

// Static i18n keys per level — the ember skill forbids dynamic template-literal
// key construction. Same shape as the macro composer picker.
const LEVEL_LABEL_KEYS: Record<PriorityLevelName, string> = {
  none: 'inbox.command-k.set-priority-level.none',
  low: 'inbox.command-k.set-priority-level.low',
  medium: 'inbox.command-k.set-priority-level.medium',
  high: 'inbox.command-k.set-priority-level.high',
  urgent: 'inbox.command-k.set-priority-level.urgent',
};

export default class SearchFiltersPrioritySelector extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare session: Session;

  // In-popover pending selection. Cleared on applyFilter so the getter falls
  // back to the @selected arg, keeping the chip in sync when external navigation
  // (saved views, browser history) updates the predicate.
  @tracked private localSelectedOverride?: PrioritySelectorValue | typeof NONE_SENTINEL;

  get selectedPriority(): PrioritySelectorValue | typeof NONE_SENTINEL {
    return this.localSelectedOverride ?? this.args.selected ?? NONE_SENTINEL;
  }

  // Tracks only the in-progress comparison while the popover is open. Cleared
  // after each applyFilter so the getter falls back to @selectedComparison arg,
  // keeping the chip in sync when an external navigation updates the predicate.
  @tracked private localComparisonOverride?: PriorityComparison;

  get selectedComparison(): PriorityComparison {
    return this.localComparisonOverride ?? this.args.selectedComparison ?? 'eq';
  }

  get isMultiLevelEnabled() {
    return this.session.workspace.isFeatureEnabled('team-inbox-priority-levels');
  }

  get priorityLevels() {
    return PRIORITY_LEVEL_PICKER_ORDER.map((value) => {
      let display = priorityLevelDisplay(value);
      return {
        value,
        label: this.intl.t(LEVEL_LABEL_KEYS[display.name]),
      };
    });
  }

  get isCleared() {
    return this.selectedPriority === NONE_SENTINEL;
  }

  get selectedBooleanValue(): BooleanComparison | 'none' {
    let v = this.selectedPriority;
    return v === 'true' || v === 'false' ? v : 'none';
  }

  // The classic Ember Form/RadioGroup component follows two-way binding and
  // calls Ember.set(this, 'selected', value) on the parent when a radio button
  // is clicked. In Glimmer strict-mode a getter-only descriptor throws; this
  // setter intercepts that write and routes it to localSelectedOverride — the
  // same storage that onSelectBoolean uses — so state stays consistent.
  set selectedBooleanValue(value: BooleanComparison | 'none') {
    this.localSelectedOverride = value === 'none' ? NONE_SENTINEL : value;
  }

  get label() {
    this.intl.primaryLocale; // We must access the locale to make this getter aware that it is dependent on it.

    if (this.selectedPriority === NONE_SENTINEL) {
      return this.intl.t('inbox.search.filters.priority.none');
    }

    if (typeof this.selectedPriority === 'number') {
      let display = priorityLevelDisplay(this.selectedPriority);
      let levelLabel = this.intl.t(LEVEL_LABEL_KEYS[display.name]);
      if (this.selectedComparison === 'gt') {
        return `${this.intl.t('inbox.search.filters.priority.comparison.gt')} ${levelLabel}`;
      }
      if (this.selectedComparison === 'lt') {
        return `${this.intl.t('inbox.search.filters.priority.comparison.lt')} ${levelLabel}`;
      }
      return levelLabel;
    }

    return this.selectedPriority === 'true'
      ? this.intl.t('inbox.search.filters.priority.true')
      : this.intl.t('inbox.search.filters.priority.false');
  }

  @action onSelectBoolean(value: BooleanComparison) {
    this.localSelectedOverride = value;
    // applyFilter is called by the @onHide handler after popover.hide() fires
    // (template: @onSelect={{queue this.onSelectBoolean popover.hide}})
  }

  @action onSelectLevel(value: number) {
    this.localSelectedOverride = value;
    // applyFilter is called by the @onHide handler after popover.hide() fires
    // (template: @onSelect={{queue this.onSelectLevel popover.hide}})
    // Calling applyFilter here would clear localComparisonOverride before @onHide
    // fires its own applyFilter, causing gt/lt to silently revert to eq.
  }

  @action onComparisonSelect(comparison: PriorityComparison) {
    this.localComparisonOverride = comparison;
    // applyFilter is called by the @onHide handler when the popover closes.
    // Calling it here would clear localComparisonOverride before @onHide fires,
    // causing the hide handler to fall back to the stale @selectedComparison arg
    // (if the route transition hasn't resolved yet) and silently revert the
    // comparator back to the old value.
  }

  @action clear() {
    // Use NONE_SENTINEL (not undefined) so the getter doesn't fall through to
    // args.selected — we want to explicitly override the parent's active filter.
    this.localSelectedOverride = NONE_SENTINEL;
    this.localComparisonOverride = undefined;
    // applyFilter is called by the @onHide handler after popover.hide() fires
    // (template: {{on "click" (queue this.clear popover.hide)}})
  }

  @action applyFilter() {
    // Capture both values BEFORE clearing overrides — clearing localSelectedOverride
    // first would cause selectedPriority to fall back to @selected (wrong if args
    // haven't been updated yet by the parent's route transition).
    let priority = this.selectedPriority;
    let comparison = this.selectedComparison;
    this.localComparisonOverride = undefined;
    this.localSelectedOverride = undefined;
    if (priority === NONE_SENTINEL) {
      this.args.onSelect();
    } else if (typeof priority === 'number') {
      this.args.onSelect(priority, comparison);
    } else {
      this.args.onSelect(priority);
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::Search::PrioritySelector': typeof SearchFiltersPrioritySelector;
    'inbox2/search/priority-selector': typeof SearchFiltersPrioritySelector;
  }
}
